import React, { useState } from 'react';
import SEO from '../components/SEO';
import '../styles/FAQ.css';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqData = [
    {
      category: "General",
      questions: [
        {
          question: "What is LogiCar?",
          answer: "LogiCar is a smart car maintenance assistant that helps you track your vehicle's service history, get AI-powered maintenance advice, and never miss important service intervals. It's designed to keep your car running smoothly and save you money on unnecessary repairs."
        },
        {
          question: "Is LogiCar free to use?",
          answer: "LogiCar offers both free and premium plans. The free plan includes basic maintenance tracking and simple reminders. Premium plans offer advanced AI features, detailed analytics, and priority support."
        }
      ]
    },
    {
      category: "Features",
      questions: [
        {
          question: "What maintenance tasks can I track?",
          answer: "You can track all types of maintenance including oil changes, tire rotations, brake service, fluid checks, filter replacements, inspections, and any custom maintenance tasks specific to your vehicle."
        },
        {
          question: "Can I track multiple vehicles?",
          answer: "Yes! Free users can track one vehicle, while premium users can track unlimited vehicles. Each vehicle has its own maintenance history, reminders, and AI recommendations tailored to that specific vehicle's needs."
        }
      ]
    },
    {
      category: "Account & Privacy",
      questions: [
        {
          question: "Is my data secure?",
          answer: "Absolutely. We use enterprise-grade encryption to protect your data. Your personal information and vehicle data are stored securely and never shared with third parties without your explicit consent."
        },
        {
          question: "Can I export my maintenance records?",
          answer: "Yes, you can export your maintenance records in various formats including PDF and CSV. This is especially useful when selling your vehicle or switching to a different service."
        }
      ]
    },
    {
      category: "Support",
      questions: [
        {
          question: "What devices are supported?",
          answer: "LogiCar works on all modern devices including smartphones (iOS and Android), tablets, and computers. You can access your account from any device with an internet connection."
        },
        {
          question: "What if I have technical issues?",
          answer: "Our support team is available to help with any technical issues. You can contact us through the app, email, or our support portal. We typically respond within 24 hours."
        }
      ]
    }
  ];

  return (
    <div className="faq-page">
      <SEO 
        title="LogiCar FAQ - Frequently Asked Questions"
        description="Find answers to common questions about LogiCar's car maintenance tracking, AI features, and more."
        keywords="LogiCar FAQ, car maintenance questions, vehicle tracking help, automotive app support"
        canonical="https://www.logicarapp.com/faq"
      />
      
      <div className="container">
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about LogiCar's features and how to get the most out of your car maintenance tracking.</p>
        </div>

        <div className="faq-content">
          {faqData.map((category, categoryIndex) => (
            <div key={categoryIndex} className="faq-category">
              <h2 className="category-title">{category.category}</h2>
              <div className="faq-items">
                {category.questions.map((item, itemIndex) => {
                  const globalIndex = faqData.slice(0, categoryIndex).reduce((acc, cat) => acc + cat.questions.length, 0) + itemIndex;
                  const isOpen = openItems.has(globalIndex);
                  
                  return (
                    <div key={itemIndex} className={`faq-item ${isOpen ? 'open' : ''}`}>
                      <button 
                        className="faq-question"
                        onClick={() => toggleItem(globalIndex)}
                        aria-expanded={isOpen}
                      >
                        <span className="question-text">{item.question}</span>
                        <span className="toggle-icon">
                          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
                        </span>
                      </button>
                      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                        <div className="answer-content">
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="faq-footer">
          <div className="contact-section">
            <h3>Still have questions?</h3>
            <p>Can't find the answer you're looking for? Our support team is here to help.</p>
            <div className="contact-options">
              <a href="mailto:reply.logicar@gmail.com" className="contact-link">
                <i className="fas fa-envelope"></i>
                Email Support
              </a>
              <a href="/blog" className="contact-link">
                <i className="fas fa-blog"></i>
                Check Our Blog
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
